import { useCallback, useMemo } from 'react';
import { usePersistentState } from '../../hooks/usePersistentState';

// Se guarda como `beartrack.helpdesk.incidents.recentTypes` (usePersistentState
// agrega el prefijo). Son claves de tipo de incidencia, sin datos personales.
const RECENT_TYPES_STORAGE_KEY = 'incidents.recentTypes';

export const MAX_RECENT_INCIDENT_TYPES = 4;

/**
 * Lo guardado puede venir de otra versión o editado a mano: solo se aceptan
 * strings no vacíos, sin repetir y como mucho MAX_RECENT_INCIDENT_TYPES.
 * @param {unknown} stored
 * @returns {string[]}
 */
function sanitizeRecentTypes(stored) {
  if (!Array.isArray(stored)) return [];
  const unique = [];
  stored.forEach((type) => {
    if (typeof type === 'string' && type && !unique.includes(type)) unique.push(type);
  });
  return unique.slice(0, MAX_RECENT_INCIDENT_TYPES);
}

/**
 * Últimos tipos de incidencia creados con éxito en este navegador, del más
 * reciente al más antiguo. Si localStorage no está disponible funciona en
 * memoria (lo resuelve usePersistentState): los recientes no son esenciales.
 *
 * Quien muestra los chips descarta los tipos que ya no se pueden elegir
 * (excluidos o fuera del catálogo).
 *
 * @returns {{ recentTypes: string[], recordRecentType: (type: string) => void }}
 */
export function useRecentIncidentTypes() {
  const [stored, setStored] = usePersistentState(RECENT_TYPES_STORAGE_KEY, []);
  const recentTypes = useMemo(() => sanitizeRecentTypes(stored), [stored]);

  const recordRecentType = useCallback(
    (type) => {
      if (typeof type !== 'string' || !type) return;
      setStored((previous) => sanitizeRecentTypes([type, ...sanitizeRecentTypes(previous)]));
    },
    [setStored],
  );

  return { recentTypes, recordRecentType };
}

export default useRecentIncidentTypes;
